// src/pages/TicketDeliveryManagement/index.tsx
// Trang Giao vé cho đại lý (vai trò Nhân viên Kho/Phát hành) — README_TICKETHANDLE.md mục 4.8.
// Layout bám theo trang /issuance-plans: Card + CardHeader (tiêu đề + nút hành động) + filter Row + DataTableShell.

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Row,
  Spinner,
} from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import BreadCrumb from 'Components/Common/BreadCrumb';
import AgentPagination from 'pages/AgentManagement/AgentPagination';
import { PageActionPermission } from 'services/pageActionPermission/PageActionPermission';
import { getIssuancePlans } from 'helpers/fakebackend_helper';
import {
  AgentMap,
  buildAgentMap,
  getAgentCode,
  getAgentName,
  loadAgentsWithCache,
} from 'helpers/agentLookup';
import { IAgent } from 'slices/agentManagement/types';
import { ISSUANCE_PLAN_STATUS, IssuancePlan } from 'pages/IssuancePlan/types';
import {
  agentConfirmTicketDelivery,
  cancelTicketDelivery,
  confirmTicketDelivery,
  createTicketDelivery,
  fetchTicketDeliveries,
} from 'slices/ticketDelivery/thunk';
import {
  clearFilters,
  closeModal,
  openModal,
  setFilters,
  setPage,
  setSelectedItem,
} from 'slices/ticketDelivery/reducer';
import { useBrandingTitle } from '../../hooks';

import TicketDeliveryList from './components/TicketDeliveryList';
import CreateTicketDeliveryModal, { AgentOption } from './modals/CreateTicketDeliveryModal';
import DeliveryActionConfirmModal, {
  DeliveryActionType,
} from './modals/DeliveryActionConfirmModal';
import DeliveryDetailModal from './modals/DeliveryDetailModal';
import { buildDeliveryForPrint, printDeliverySummary } from './utils/printDeliverySummary';
import {
  CreateTicketDeliveryPayload,
  TICKET_DELIVERY_STATUS,
  TICKET_DELIVERY_STATUS_LABELS,
  TICKET_DELIVERY_STATUS_OPTIONS,
  TicketDelivery,
  calcLineQuantity,
} from './types';
import { createScreenText } from 'helpers/screenText';
const st = createScreenText('ticket_delivery_management');

const getPayloadItems = (response: any): any[] => {
  const payload = response?.data || response;
  return payload?.items || payload?.data || payload || [];
};

const TicketDeliveryManagement: React.FC = () => {
  useBrandingTitle(st('giao_ve_cho_dai_ly_text'));

  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();

  const { list, selectedItem, pagination, filters, loading, submitting, modalState } =
    useSelector((state: any) => state.TicketDelivery);

  // Mục 10.a, README_UPDATE.md — tài khoản đại lý (có agentId gắn với User) chỉ được thấy phiếu
  // SAU KHI admin đã duyệt (đã chặn ở tầng API); ẩn luôn lựa chọn "Chờ duyệt" khỏi dropdown lọc để
  // đại lý không lọc ra danh sách rỗng.
  const currentAgentId = useSelector((state: any) => state.UserProfile?.profile?.agentId);
  const isAgentAccount = !!currentAgentId;
  const statusOptions = useMemo(
    () =>
      isAgentAccount
        ? TICKET_DELIVERY_STATUS_OPTIONS.filter(
            (option) => Number(option.value) !== TICKET_DELIVERY_STATUS.Draft,
          )
        : TICKET_DELIVERY_STATUS_OPTIONS,
    [isAgentAccount],
  );

  // Danh sách kế hoạch phát hành đã ở trạng thái Accept (chỉ plan đã nhập kho mới có vé để giao)
  // — truyền thẳng vào CreateTicketDeliveryModal để chọn kế hoạch ngay trong modal (mục 12).
  const [plans, setPlans] = useState<IssuancePlan[]>([]);
  const [agents, setAgents] = useState<IAgent[]>([]);
  const [actionType, setActionType] = useState<DeliveryActionType>('confirm');
  // Mục 8.b, README_UPDATE.md — gộp màn "Quản lý vé" (TicketManagement, nút Xác nhận đại lý) vào
  // đúng trang này, quản lý bằng trạng thái phiếu thay vì tách 2 trang gần như giống hệt nhau.
  const [confirmAgentTarget, setConfirmAgentTarget] = useState<TicketDelivery | null>(null);

  const agentMap: AgentMap = useMemo(() => buildAgentMap(agents), [agents]);

  // Đến từ link trong thông báo (mục 13.2, REQUIRE_FIXBUGS.md: "...đại lý đó vào đọc thông báo link
  // đến màn Quản lý vé") — giữ nguyên query param `deliveryId` để link cũ `/ticket-management?deliveryId=`
  // vẫn hoạt động đúng sau khi gộp 2 route về cùng 1 component.
  const highlightDeliveryId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const value = params.get('deliveryId');
    return value ? Number(value) : null;
  }, [location.search]);

  // Nạp danh sách kế hoạch phát hành + đại lý 1 lần khi vào trang
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        // API IssuancePlan có hỗ trợ filter theo status — vẫn lọc lại phía client để chắc chắn
        // chỉ hiển thị plan Accept (mục 4.1, README_TICKETHANDLE.md)
        const planResponse = await getIssuancePlans({
          pageIndex: 1,
          pageSize: 200,
          status: ISSUANCE_PLAN_STATUS.Accept,
        });
        const planItems = getPayloadItems(planResponse) as IssuancePlan[];
        setPlans(
          planItems.filter((plan) => Number(plan.status) === ISSUANCE_PLAN_STATUS.Accept),
        );
      } catch (error: any) {
        toast.error(error?.message || st('khong_the_tai_danh_sach_ke_message'));
      }

      // Danh sách đại lý dùng cho dropdown lọc + map agentId -> mã/tên khi render bảng
      // (DTO của ticket-service chỉ trả agentId). Form lập phiếu dùng autocomplete gọi API riêng.
      setAgents(await loadAgentsWithCache());
    };

    loadReferenceData();
  }, []);

  const loadDeliveries = useCallback(() => {
    dispatch(
      fetchTicketDeliveries({
        pageIndex: pagination.currentPage,
        pageSize: pagination.pageSize,
        planId: filters.planId || undefined,
        agentId: filters.agentId || undefined,
        status: filters.status === 'All' ? undefined : filters.status,
      }) as any,
    );
  }, [dispatch, pagination.currentPage, pagination.pageSize, filters]);

  useEffect(() => {
    loadDeliveries();
  }, [loadDeliveries]);

  const handleCreate = () => {
    dispatch(openModal('create'));
  };

  const handleCreateSubmit = async (
    payload: CreateTicketDeliveryPayload,
    agent: AgentOption | null,
  ) => {
    const result = await dispatch(createTicketDelivery(payload) as any);
    if (createTicketDelivery.fulfilled.match(result)) {
      toast.success(st('lap_phieu_giao_ve_thanh_cong_message'));
      loadDeliveries();

      // Mở luôn bản tóm tắt phiếu để in / lưu PDF. Dữ liệu ưu tiên lấy từ response; nếu API chỉ
      // trả về id/mã phiếu thì bù bằng đúng dữ liệu người dùng vừa nhập trên form.
      const lines = payload.lines.map((line) => ({
        ...line,
        quantity: calcLineQuantity(line),
      }));
      const createdPlan = plans.find((plan) => plan.id === payload.issuancePlanId);
      printDeliverySummary(
        buildDeliveryForPrint(result.payload, {
          issuancePlanId: payload.issuancePlanId,
          planCode: createdPlan?.planCode,
          agentId: payload.agentId,
          status: TICKET_DELIVERY_STATUS.Draft,
          statusName: TICKET_DELIVERY_STATUS_LABELS[String(TICKET_DELIVERY_STATUS.Draft)],
          note: payload.note,
          totalQuantity: lines.reduce((sum, line) => sum + line.quantity, 0),
          lines,
        }),
        { code: agent?.code, name: agent?.name },
      );
    } else {
      toast.error((result.payload as string) || st('khong_the_lap_phieu_giao_ve_message'));
    }
  };

  const handleViewDetail = (delivery: TicketDelivery) => {
    dispatch(setSelectedItem(delivery));
    dispatch(openModal('detail'));
  };

  const handleConfirmClick = (delivery: TicketDelivery) => {
    setActionType('confirm');
    dispatch(setSelectedItem(delivery));
    dispatch(openModal('confirm'));
  };

  const handleCancelClick = (delivery: TicketDelivery) => {
    setActionType('cancel');
    dispatch(setSelectedItem(delivery));
    dispatch(openModal('cancel'));
  };

  const handleAgentConfirmClick = (delivery: TicketDelivery) => {
    setConfirmAgentTarget(delivery);
  };

  const handleAgentConfirmSubmit = async () => {
    if (!confirmAgentTarget) return;
    const result = await dispatch(agentConfirmTicketDelivery(confirmAgentTarget.id) as any);
    if (agentConfirmTicketDelivery.fulfilled.match(result)) {
      toast.success(st('da_xac_nhan_dai_ly_nhan_message'));
      setConfirmAgentTarget(null);
      loadDeliveries();
    } else {
      toast.error((result.payload as string) || st('khong_the_xac_nhan_dai_ly_message'));
    }
  };

  const handleActionConfirm = async (reason?: string) => {
    if (!selectedItem?.id) return;

    if (actionType === 'confirm') {
      const confirmedItem = selectedItem;
      const result = await dispatch(confirmTicketDelivery(confirmedItem.id) as any);
      if (confirmTicketDelivery.fulfilled.match(result)) {
        toast.success(st('xac_nhan_giao_ve_thanh_cong_message'));
        loadDeliveries();

        // In / lưu PDF phiếu giao vé đã xác nhận
        printDeliverySummary(buildDeliveryForPrint(result.payload, confirmedItem), {
          code: getAgentCode(agentMap, confirmedItem.agentId),
          name: getAgentName(agentMap, confirmedItem.agentId),
        });
      } else {
        toast.error((result.payload as string) || st('khong_the_xac_nhan_giao_ve_message'));
      }
      return;
    }

    const result = await dispatch(
      cancelTicketDelivery({ id: selectedItem.id, reason }) as any,
    );
    if (cancelTicketDelivery.fulfilled.match(result)) {
      toast.success(st('huy_phieu_giao_ve_thanh_cong_message'));
      loadDeliveries();
    } else {
      toast.error((result.payload as string) || st('khong_the_huy_phieu_giao_ve_message'));
    }
  };

  const isActionModalOpen = modalState.confirm || modalState.cancel;

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <BreadCrumb title={st('giao_ve_cho_dai_ly_text')} pageTitle={st('quan_ly_phat_hanh_text')} />

          {highlightDeliveryId && (
            <Alert color="info" className="py-2">
              <i className="ri-notification-3-line me-1"></i>
              {st('dang_mo_tu_thong_bao_phieu_message')}{highlightDeliveryId} {st('duoc_to_sang_ben_duoi_message')}
            </Alert>
          )}

          {/* Khối 1: chọn kế hoạch phát hành để lập phiếu giao vé — bảng tồn kho tách sang màn
              riêng "Tồn kho vé theo kế hoạch" (xem REQUIRE_FIXBUGS.md mục 8/10), màn này chỉ giữ
              phần chọn kế hoạch (cần cho việc lập phiếu) + danh sách phiếu đã giao ở khối 2. */}
          {/* Danh sách phiếu giao đã lập — nút "Lập phiếu giao" mở modal chọn kế hoạch phát hành
              ngay bên trong (mục 12, REQUIRE_FIXBUGS.md), không còn card chọn kế hoạch riêng. */}
          <Card id="ticketDeliveryList">
            <CardHeader className="card-header border-0">
              <Row className="align-items-center gy-3">
                <div className="col-sm">
                  <h5 className="card-title mb-0">{st('danh_sach_phieu_giao_ve_title')}</h5>
                </div>
                <div className="col-sm-auto d-flex gap-2">
                  <button
                    type="button"
                    className="btn btn-soft-secondary"
                    disabled={loading}
                    onClick={() => dispatch(clearFilters())}
                  >
                    <i className="ri-refresh-line align-bottom me-1"></i>
                    {st('xoa_loc_button')}
                  </button>
                  {/* Sang màn quét QR chuyên dụng (số lượng lớn, nhiều ký hiệu — camera quét liên tục). */}
                  <button
                    type="button"
                    className="btn btn-soft-primary"
                    data-action-code="button-qr-scan"
                    data-action-name="Giao vé bằng QR"
                    onClick={() => navigate('/ticket-delivery-management/qr-scan')}
                  >
                    <i className="ri-qr-scan-2-line align-bottom me-1"></i>
                    {st('giao_ve_bang_qr_button')}
                  </button>
                  {PageActionPermission.canDisplay(location.pathname, 'button-add') && (
                    <button
                      type="button"
                      className="btn btn-success add-btn"
                      data-action-code="button-add"
                      data-action-name="Lập phiếu giao vé"
                      onClick={handleCreate}
                    >
                      <i className="ri-add-line align-bottom me-1"></i>
                      {st('lap_phieu_giao_button')}
                    </button>
                  )}
                </div>
              </Row>
            </CardHeader>
            <CardBody className="pt-0">
              <Row className="g-3 mb-3 align-items-end mb-5">
                <Col md={3}>
                  <Label className="form-label">{st('ke_hoach_phat_hanh_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.planId}
                    data-action-code="filter-input-plan"
                    data-action-name="Lọc theo kế hoạch phát hành"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-plan')}
                    onChange={(event) => dispatch(setFilters({ planId: event.target.value }))}
                  >
                    <option value="">{st('tat_ca_ke_hoach_option')}</option>
                    {plans.map((plan) => (
                      <option key={plan.id} value={plan.id}>
                        {plan.planCode}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('dai_ly_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.agentId}
                    data-action-code="filter-input-agent"
                    data-action-name="Lọc theo đại lý"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-agent')}
                    onChange={(event) => dispatch(setFilters({ agentId: event.target.value }))}
                  >
                    <option value="">{st('tat_ca_dai_ly_option')}</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.code} - {agent.name}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3}>
                  <Label className="form-label">{st('trang_thai_column')}</Label>
                  <Input
                    type="select"
                    className="form-select"
                    value={filters.status}
                    data-action-code="filter-input-status"
                    data-action-name="Lọc theo trạng thái phiếu giao"
                    disabled={!PageActionPermission.canEnable(location.pathname, 'filter-input-status')}
                    onChange={(event) => dispatch(setFilters({ status: event.target.value }))}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Input>
                </Col>
                <Col md={3} className="text-md-end">
                  <span className="text-muted">
                    {st('tong_text')} <span className="fw-semibold">{pagination.totalItems}</span> {st('phieu_text')}
                  </span>
                </Col>
              </Row>

              <TicketDeliveryList
                deliveries={list}
                pagination={pagination}
                agentMap={agentMap}
                loading={loading}
                onViewDetail={handleViewDetail}
                onConfirm={handleConfirmClick}
                onCancel={handleCancelClick}
                onAgentConfirm={handleAgentConfirmClick}
                highlightDeliveryId={highlightDeliveryId}
              />

              <AgentPagination
                currentPage={pagination.currentPage}
                totalPages={pagination.totalPages}
                totalItems={pagination.totalItems}
                pageSize={pagination.pageSize}
                onPageChange={(page) => dispatch(setPage(page))}
              />
            </CardBody>
          </Card>
        </Container>
      </div>

      {modalState.create && (
        <CreateTicketDeliveryModal
          isOpen={modalState.create}
          submitting={submitting}
          plans={plans}
          onClose={() => dispatch(closeModal('create'))}
          onSubmit={handleCreateSubmit}
        />
      )}

      {isActionModalOpen && (
        <DeliveryActionConfirmModal
          isOpen={isActionModalOpen}
          action={actionType}
          delivery={selectedItem}
          agentMap={agentMap}
          loading={submitting}
          onClose={() => {
            dispatch(closeModal(actionType));
            dispatch(setSelectedItem(null));
          }}
          onConfirm={handleActionConfirm}
        />
      )}

      {modalState.detail && (
        <DeliveryDetailModal
          isOpen={modalState.detail}
          delivery={selectedItem}
          agentMap={agentMap}
          onPrint={() =>
            selectedItem &&
            printDeliverySummary(selectedItem, {
              code: getAgentCode(agentMap, selectedItem.agentId),
              name: getAgentName(agentMap, selectedItem.agentId),
            })
          }
          onClose={() => {
            dispatch(closeModal('detail'));
            dispatch(setSelectedItem(null));
          }}
        />
      )}

      <Modal isOpen={!!confirmAgentTarget} toggle={() => setConfirmAgentTarget(null)} centered>
        <ModalHeader toggle={() => setConfirmAgentTarget(null)}>{st('xac_nhan_dai_ly_da_nhan_title')}</ModalHeader>
        <ModalBody>
          {st('xac_nhan_dai_ly_button')}{' '}
          <strong>
            {confirmAgentTarget ? getAgentName(agentMap, confirmAgentTarget.agentId) : ''}
          </strong>{' '}
          {st('da_nhan_du_ve_cua_phieu_text')} <strong>{confirmAgentTarget?.deliveryCode}</strong>{st('thao_tac_nay_chi_doi_trang_text')}
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={() => setConfirmAgentTarget(null)} disabled={submitting}>
            {st('dong_button')}
          </Button>
          <Button color="primary" onClick={handleAgentConfirmSubmit} disabled={submitting}>
            {submitting ? (
              <>
                <Spinner size="sm" className="me-2" />
                {st('dang_xu_ly_text')}
              </>
            ) : (
              st('xac_nhan_text')
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </React.Fragment>
  );
};

export default TicketDeliveryManagement;
